import { Skeleton } from "antd";
import { documentExpiresInOptions, documentExpiryStatusOptions } from "utils/constants";

// Each tile is a one-click expiry filter. `filter` is the exact pair of
// filter values the tile represents, so the selected tile is the one whose
// pair matches the applied filters and nothing is highlighted for other
// combinations (e.g. an expiry_in=this_month URL, or status + window set
// together in the drawer).
export const STAT_TILES = [
  { key: "total", label: "All documents", statKey: "total", tone: "neutral", filter: { expiry_status: "", expiry_in: "" } },
  { key: "active", label: "Active", statKey: "active", tone: "success", filter: { expiry_status: "active", expiry_in: "" } },
  { key: "expired", label: "Expired", statKey: "expired", tone: "danger", filter: { expiry_status: "expired", expiry_in: "" } },
  { key: "today", label: "Expiring today", statKey: "expiring_today", tone: "danger", filter: { expiry_status: "", expiry_in: "today" } },
  { key: "7", label: "Within 7 days", statKey: "expiring_in_7_days", tone: "warning", filter: { expiry_status: "", expiry_in: "7" } },
  { key: "30", label: "Within 30 days", statKey: "expiring_in_30_days", tone: "info", filter: { expiry_status: "", expiry_in: "30" } },
  { key: "60", label: "Within 60 days", statKey: "expiring_in_60_days", tone: "info", filter: { expiry_status: "", expiry_in: "60" } },
  { key: "90", label: "Within 90 days", statKey: "expiring_in_90_days", tone: "info", filter: { expiry_status: "", expiry_in: "90" } },
];

const TONES = {
  neutral: { accent: "#6b7280", tint: "#f3f4f6" },
  success: { accent: "#16a34a", tint: "#dcfce7" },
  danger: { accent: "#dc2626", tint: "#fee2e2" },
  warning: { accent: "#d97706", tint: "#fef3c7" },
  info: { accent: "#2563eb", tint: "#dbeafe" },
};

const optionFor = (options, value) => options.find((o) => String(o.value) === String(value));

// Translate a tile into the filter entries `applyFilterValues` expects.
// Both keys are always sent so picking a tile replaces any other expiry
// filter instead of stacking on top of it.
export const tileToFilterEntries = (tile) => [
  { key: "expiry_status", data: optionFor(documentExpiryStatusOptions, tile.filter.expiry_status) || "" },
  { key: "expiry_in", data: optionFor(documentExpiresInOptions, tile.filter.expiry_in) || "" },
];

// `applied` is the expiry pair the list was last fetched with (taken from
// the URL), deliberately not the drawer's unapplied draft.
export const selectedTileKey = (applied) => {
  const status = String(applied?.expiry_status || "");
  const within = String(applied?.expiry_in || "");
  return STAT_TILES.find((t) => t.filter.expiry_status === status && t.filter.expiry_in === within)?.key || null;
};

const DocumentStatsBar = ({ stats, loading, applied, onSelect, onRetry }) => {
  const selected = selectedTileKey(applied);
  const failed = !loading && !stats;

  return (
    <div style={{ marginBottom: "16px" }}>
      <style>{`
        /* Four per row on laptop widths (two balanced rows), eight across on
           wide screens; never an orphan row of two. */
        .doc-stat-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 8px;
        }
        @media (min-width: 1500px) {
          .doc-stat-grid { grid-template-columns: repeat(8, minmax(0, 1fr)); }
        }
        .doc-stat-tile {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
          width: 100%;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          border-left-width: 3px;
          background: #ffffff;
          text-align: left;
          cursor: pointer;
          transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
        }
        .doc-stat-tile:hover { border-color: #d1d5db; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06); }
        .doc-stat-tile:focus-visible { outline: 2px solid rgb(211, 0, 26); outline-offset: 2px; }
        .doc-stat-tile[aria-pressed="true"] { box-shadow: 0 0 0 2px var(--tile-accent) inset; }
        .doc-stat-label {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #6b7280;
          white-space: nowrap;
        }
        .doc-stat-count { font-size: 20px; font-weight: 700; line-height: 1.2; color: #111827; }
        .doc-stat-count.is-zero { color: #6b7280; }
        @media (max-width: 768px) {
          /* One swipeable row instead of four stacked rows above the list. */
          .doc-stat-grid {
            display: flex;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 4px;
          }
          .doc-stat-tile { flex: 0 0 auto; width: 136px; scroll-snap-align: start; }
        }
      `}</style>
      <div className="doc-stat-grid" role="group" aria-label="Document expiry summary">
        {STAT_TILES.map((tile) => {
          const tone = TONES[tile.tone];
          const isSelected = selected === tile.key;
          const count = stats ? stats[tile.statKey] : null;
          return (
            <button
              key={tile.key}
              type="button"
              className="doc-stat-tile"
              aria-pressed={isSelected}
              onClick={() => onSelect?.(tile)}
              style={{
                "--tile-accent": tone.accent,
                borderLeftColor: tone.accent,
                borderColor: isSelected ? tone.accent : undefined,
                background: isSelected ? tone.tint : "#ffffff",
              }}
            >
              <span className="doc-stat-label">{tile.label}</span>
              {loading && !stats ? (
                <Skeleton.Button active size="small" style={{ width: 48, height: 22 }} />
              ) : (
                <span className={`doc-stat-count${count === 0 ? " is-zero" : ""}`}>
                  {count === null || count === undefined ? "–" : count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {failed && (
        <div role="status" style={{ marginTop: "6px", fontSize: "12px", color: "#b91c1c" }}>
          Summary counts are unavailable.{" "}
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="btn btn-link p-0"
              style={{ fontSize: "12px", fontWeight: 600, color: "rgb(211, 0, 26)", verticalAlign: "baseline" }}
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentStatsBar;
